import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  productsList: [],
  movementHistory: [],

  isAdjustModalOpen: false,
  isHistoryModalOpen: false,
  isSubmitting: false,
  isLoadingHistory: false,

  activeProduct: null,

  // Added unit_cost to payload
  adjustForm: {
    type: "IN",
    quantity: 1,
    origin: "adjustment",
    unit_cost: "",
    notes: "",
  },

  productsDataTable: null,

  async initStock() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      await this.loadProductsTable();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  // ================= HELPERS DE FORMATAÇÃO =================

  formatPrice(price) {
    if (!price) return "-";
    return parseFloat(price).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },

  applyMoneyMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  handleCostInput(event) {
    this.adjustForm.unit_cost = this.applyMoneyMask(event.target.value);
  },

  unmaskMoney(maskedValue) {
    if (!maskedValue) return null;
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  // ================= DATA TABLE =================

  async loadProductsTable() {
    try {
      const response = await axios.get("/api/client/products");
      this.productsList = response.data.data;

      if (this.productsDataTable) {
        this.productsDataTable.destroy();
      }

      const tbody = document.querySelector("#stockTable tbody");
      tbody.innerHTML = this.productsList
        .map((product) => {
          const stockDisplay =
            product.stock > 0
              ? `<span class="font-medium text-slate-700">${product.stock} un.</span>`
              : `<span class="font-bold text-red-600">Esgotado</span>`;

          return `
            <tr>
              <td class="py-4 px-4 xl:px-6 font-bold text-slate-800">
                ${product.name}
              </td>
              <td class="py-4 px-4 xl:px-6">
                ${stockDisplay}
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('open-adjust', {detail: ${product.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none"
                  >
                    Ajustar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('open-history', {detail: ${product.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 border border-slate-300 shadow-sm transition-colors hover:bg-slate-200 focus:outline-none"
                  >
                    Extrato
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.productsDataTable = new DataTable("#stockTable", {
        searchable: false,
        perPageSelect: false,
        sortable: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} produtos",
          noRows: "Nenhum produto cadastrado",
          noResults: "Nenhum resultado encontrado",
          searchTitle: "Pesquisar produto...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= MODAL: ADJUSTMENT =================

  openAdjust(productId) {
    this.activeProduct = this.productsList.find((p) => p.id === productId);
    this.adjustForm = {
      type: "IN",
      quantity: 1,
      origin: "adjustment",
      unit_cost: "",
      notes: "",
    };
    this.isAdjustModalOpen = true;
  },

  // Handle type change to reset origin and prevent validation errors
  handleTypeChange() {
    this.adjustForm.origin = "adjustment";
    if (this.adjustForm.type === "OUT") {
      this.adjustForm.unit_cost = ""; // Clear cost when outputting
    }
  },

  async submitAdjustment() {
    this.isSubmitting = true;
    try {
      const payload = {
        ...this.adjustForm,
        unit_cost: this.unmaskMoney(this.adjustForm.unit_cost),
      };

      await axios.post(
        `/api/client/stock/${this.activeProduct.id}/adjust`,
        payload,
      );
      showAlert("Estoque ajustado com sucesso!", "success");

      await this.loadProductsTable();
      this.isAdjustModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao ajustar o estoque.");
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= MODAL: HISTORY =================

  async openHistory(productId) {
    this.activeProduct = this.productsList.find((p) => p.id === productId);
    this.isHistoryModalOpen = true;
    this.isLoadingHistory = true;
    this.movementHistory = [];

    try {
      const response = await axios.get(
        `/api/client/stock/${this.activeProduct.id}`,
      );
      this.movementHistory = response.data.data.data;
    } catch (error) {
      handleRequestError(error, "Erro ao carregar o histórico.");
    } finally {
      this.isLoadingHistory = false;
    }
  },

  translateOrigin(origin) {
    const origins = {
      sale: "Venda (PDV)",
      adjustment: "Ajuste Manual",
      loss: "Perda/Avaria",
      initial_load: "Carga Inicial",
      return: "Devolução",
      purchase: "Compra (Fornecedor)",
    };
    return origins[origin] || origin;
  },

  formatDate(dateString) {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("pt-BR") +
      " às " +
      date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    );
  },
});
